import { useState, useEffect } from 'react';
import { Save, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { systemConfigService, operationLogService } from '@/services/storage';
import type { SystemConfig } from '@/types';
import { toast } from 'sonner';

export default function AdminSystemConfig() {
  const [config, setConfig] = useState<SystemConfig | null>(null);

  useEffect(() => {
    const loadConfig = async () => {
      setConfig(await systemConfigService.get());
    };
    loadConfig();
  }, []);

  const handleSave = async () => {
    if (!config) return;
    await systemConfigService.save(config);
    await operationLogService.add({
      user: 'admin',
      action: '更新系统配置',
      module: '系统配置',
      details: '更新了系统基础配置和邮件配置',
    });
    toast.success('系统配置已保存');
  };

  const handleChange = (field: keyof SystemConfig, value: string | boolean) => {
    setConfig(prev => prev ? ({ ...prev, [field]: value }) : null);
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">系统配置</h1>
        <Button onClick={handleSave}>
          <Save className="mr-2 h-5 w-5" />
          保存配置
        </Button>
      </div>

      <Tabs defaultValue="basic">
        <TabsList className="mb-6">
          <TabsTrigger value="basic">
            <Globe className="mr-2 h-4 w-4" />
            基础配置
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="mr-2 h-4 w-4" />
            邮件配置
          </TabsTrigger>
        </TabsList>

        {/* 基础配置 */}
        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle>站点基础信息</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="siteName">站点名称</Label>
                <Input
                  id="siteName"
                  value={config.siteName || ''}
                  onChange={(e) => handleChange('siteName', e.target.value)}
                  placeholder="如：瀚昕微电子"
                />
              </div>

              <div>
                <Label htmlFor="siteKeywords">站点关键词</Label>
                <Input
                  id="siteKeywords"
                  value={config.siteKeywords || ''}
                  onChange={(e) => handleChange('siteKeywords', e.target.value)}
                  placeholder="如：电源管理芯片,LDO,DCDC,充电IC"
                />
                <p className="text-xs text-gray-500 mt-1">多个关键词用逗号分隔，用于SEO优化</p>
              </div>

              <div>
                <Label htmlFor="siteDescription">站点描述</Label>
                <textarea
                  id="siteDescription"
                  className="w-full border rounded-md px-3 py-2"
                  rows={3}
                  value={config.siteDescription || ''}
                  onChange={(e) => handleChange('siteDescription', e.target.value)}
                  placeholder="如：瀚昕微电子是一家专注于高性能、高品质数模混合芯片研发的芯片设计公司"
                />
                <p className="text-xs text-gray-500 mt-1">用于SEO优化，建议控制在150字以内</p>
              </div>

              <div>
                <Label htmlFor="icpNumber">备案号</Label>
                <Input
                  id="icpNumber"
                  value={config.icpNumber || ''}
                  onChange={(e) => handleChange('icpNumber', e.target.value)}
                  placeholder="如：苏ICP备XXXXXXXX号"
                />
                <p className="text-xs text-gray-500 mt-1">填写后将在网站底部显示备案信息</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 邮件配置 */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <CardTitle>SMTP邮件服务器配置</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="smtpHost">SMTP服务器地址</Label>
                  <Input
                    id="smtpHost"
                    value={config.smtpHost || ''}
                    onChange={(e) => handleChange('smtpHost', e.target.value)}
                    placeholder="如：smtp.example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="smtpPort">SMTP端口</Label>
                  <Input
                    id="smtpPort"
                    value={config.smtpPort || ''}
                    onChange={(e) => handleChange('smtpPort', e.target.value)}
                    placeholder="如：587"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="smtpUser">SMTP用户名</Label>
                <Input
                  id="smtpUser"
                  value={config.smtpUser || ''}
                  onChange={(e) => handleChange('smtpUser', e.target.value)}
                  placeholder="如：noreply@hypwr.com"
                />
              </div>

              <div>
                <Label htmlFor="smtpPassword">SMTP密码</Label>
                <Input
                  id="smtpPassword"
                  type="password"
                  value={config.smtpPassword || ''}
                  onChange={(e) => handleChange('smtpPassword', e.target.value)}
                  placeholder="请输入SMTP密码"
                />
              </div>

              <div>
                <Label htmlFor="notifyEmail">通知接收邮箱</Label>
                <Input
                  id="notifyEmail"
                  value={config.notifyEmail || ''}
                  onChange={(e) => handleChange('notifyEmail', e.target.value)}
                  placeholder="如：sales@hypwr.com"
                />
                <p className="text-xs text-gray-500 mt-1">询单、简历投递等通知将发送到此邮箱</p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="smtpSecure"
                  checked={config.smtpSecure}
                  onChange={(e) => handleChange('smtpSecure', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="smtpSecure" className="mb-0">使用SSL/TLS加密连接</Label>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>提示：</strong>当前为前端演示版本，邮件发送功能需要配合后端服务实现。
                  配置信息将被保存，实际邮件发送需要在后端配置SMTP服务。
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
